import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/acp-mobile-sdkdocs/acp-mobile-sdkdocs/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import Tabs from './tabs/index.md';
import Alerts from '../../resources/alerts.md';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const TabsBlock = makeShortcode("TabsBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "configuration"
    }}>{`Configuration`}</h1>
    <Alerts query="platform=outdated-version&componentClass=InlineNestedAlert" mdxType="Alerts" />
    <p>{`The Configuration extension is built into the Mobile Core extension. It provides several different APIs for you to setup the configuration either remotely in the Data Collection UI or locally.`}</p>
    <h2 {...{
      "id": "configure-with-app-id-per-environment"
    }}>{`Configure with App ID per environment`}</h2>
    <p>{`When you configure a mobile property, a unique environment ID is generated that the SDK uses to retrieve your configuration. This ID is generated when an app configuration is created and published to a given environment. The app is first launched and then the SDK retrieves and uses this Adobe-hosted configuration.`}</p>
    <InlineAlert variant="success" slots="text" mdxType="InlineAlert" />
    <p>{`As best practice, you should configure a mobile property in the Data Collection UI and use environment IDs to configure your application. Follow the steps in the tutorial on `}<a parentName="p" {...{
        "href": "../../getting-started/create-a-mobile-property.md"
      }}>{`setting up a mobile property`}</a>{` if you need to create a new Experience Platform App.`}</p>
    <p>{`After the configuration is retrieved when the app is initially launched, the configuration is stored in local cache. The SDK tries to refresh the configuration every cold launch or when a new session is detected. If there is no change or a network request error occurs while downloading the configuration file, the cached configuration will be used.`}</p>
    <p>{`The unique environment ID from the Data Collection UI can be configured with the SDK using the following:`}</p>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="5" mdxType="TabsBlock" />
    <p>{`Android`}</p>
    <Tabs query="platform=android&task=configure" mdxType="Tabs" />
    <p>{`iOS`}</p>
    <Tabs query="platform=ios&task=configure" mdxType="Tabs" />
    <p>{`Cordova`}</p>
    <Tabs query="platform=cordova&task=configure" mdxType="Tabs" />
    <p>{`Unity`}</p>
    <Tabs query="platform=unity&task=configure" mdxType="Tabs" />
    <p>{`Xamarin`}</p>
    <Tabs query="platform=xamarin&task=configure" mdxType="Tabs" />
    <h2 {...{
      "id": "programmatic-updates-to-configuration"
    }}>{`Programmatic updates to configuration`}</h2>
    <p>{`You can also update the configuration programmatically by passing configuration keys and values to override the existing configuration.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Keys that are not found on the current configuration are added when this method is followed. Null values are allowed and replace existing configuration values.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`Do not use this API to update the build.environment or any key with an environment prefix, because it can lead to unexpected behavior. For more information, read `}<a parentName="p" {...{
        "href": "./index.md#environment-aware-configuration-properties"
      }}>{`Environment-aware configuration properties`}</a>{`.`}</p>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="7" mdxType="TabsBlock" />
    <p>{`Android`}</p>
    <Tabs query="platform=android&task=update" mdxType="Tabs" />
    <p>{`iOS`}</p>
    <Tabs query="platform=ios&task=update" mdxType="Tabs" />
    <p>{`React Native`}</p>
    <Tabs query="platform=react-native&task=update" mdxType="Tabs" />
    <p>{`Flutter`}</p>
    <Tabs query="platform=flutter&task=update" mdxType="Tabs" />
    <p>{`Cordova`}</p>
    <Tabs query="platform=cordova&task=update" mdxType="Tabs" />
    <p>{`Unity`}</p>
    <Tabs query="platform=unity&task=update" mdxType="Tabs" />
    <p>{`Xamarin`}</p>
    <Tabs query="platform=xamarin&task=update" mdxType="Tabs" />
    <h2 {...{
      "id": "clearing-programmatic-updates-to-the-configuration"
    }}>{`Clearing programmatic updates to the configuration`}</h2>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`This API is only available in Android.`}</p>
    <p>{`You can clear any programmatic updates made to the configuration via the `}<inlineCode parentName="p">{`clearUpdatedConfiguration`}</inlineCode>{` API. This will clear programmatic updates to configuration made via the `}<inlineCode parentName="p">{`updateConfiguration`}</inlineCode>{` API. It will also clear any updates to the `}<inlineCode parentName="p">{`MobilePrivacyStatus`}</inlineCode>{` made via `}<inlineCode parentName="p">{`setPrivacyStatus`}</inlineCode>{`.`}</p>
    <p>{`For implementation details, please refer to the `}<a parentName="p" {...{
        "href": "./api-reference.md#clearUpdatedConfiguration"
      }}>{`configuration API reference`}</a>{`.`}</p>
    <h2 {...{
      "id": "using-a-bundled-file-configuration"
    }}>{`Using a bundled file configuration`}</h2>
    <p>{`You can include a bundled JSON configuration file in your app package to replace or complement the configuration that was downloaded by using the `}<a parentName="p" {...{
        "href": "./index.md#configure-with-app-id-per-environment"
      }}>{`Configure with App ID per environment`}</a>{` approach.`}</p>
    <p>{`To download the JSON configuration file, use the following URL:`}</p>
    <p><inlineCode parentName="p">{`https://assets.adobedtm.com/PASTE-ENVIRONMENT-ID.json`}</inlineCode></p>
    <ul>
      <li parentName="ul">{`In iOS, the `}<inlineCode parentName="li">{`ADBMobileConfig.json`}</inlineCode>{` file can be placed anywhere that it is accessible in your bundle.`}</li>
      <li parentName="ul">{`In Android, the `}<inlineCode parentName="li">{`ADBMobileConfig.json`}</inlineCode>{` file must be placed in the assets folder.`}</li>
    </ul>
    <p>{`You can also load a different `}<inlineCode parentName="p">{`ADBMobileConfig.json`}</inlineCode>{` file by using the `}<inlineCode parentName="p">{`ConfigureWithFileInPath`}</inlineCode>{` method. The Adobe Experience Platform SDKs will attempt to load the file from the given path and parse its JSON contents. Previous programmatic configuration changes that were set by using the `}<inlineCode parentName="p">{`UpdateConfiguration`}</inlineCode>{` method are applied on the bundled file's configuration before setting the new configuration to the Adobe Experience Platform SDKs. If a file-read error or JSON parsing error occurs, no configuration changes are made.`}</p>
    <p>{`To pass in a bundled path and file name:`}</p>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="3" mdxType="TabsBlock" />
    <p>{`Android`}</p>
    <Tabs query="platform=android&task=bundle" mdxType="Tabs" />
    <p>{`iOS`}</p>
    <Tabs query="platform=ios&task=bundle" mdxType="Tabs" />
    <p>{`Xamarin`}</p>
    <Tabs query="platform=xamarin&task=bundle" mdxType="Tabs" />
    <h2 {...{
      "id": "environment-aware-configuration-properties"
    }}>{`Environment-aware configuration properties`}</h2>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`This feature is available starting with iOS ACPCore version 2.0.3 or above.`}</p>
    <p>{`Some extension developers might use different configuration values based on their environment, and the generated configuration might have several entries for the same property. For example, the Adobe Campaign Standard extension has different endpoints for development, staging, and production servers. Here is an example of a raw configuration that supports multiple build environments:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  "myExtension.server": "mydomain.com",
  "__dev__myExtension.server": "mydomain.dev.com",
  "__stage__myExtension.server": "mydomain.stage.com"
}
`}</code></pre>
    <InlineAlert variant="success" slots="text" mdxType="InlineAlert" />
    <p>{`Each time a remote configuration is generated in the Data Collection UI, a `}<inlineCode parentName="p">{`build.environment`}</inlineCode>{` value is set. This value is based on the environment that you are publishing. When the remote configuration is downloaded, the Configuration extension considers the value in `}<inlineCode parentName="p">{`build.environment`}</inlineCode>{` and provides `}<strong parentName="p">{`only`}</strong>{` the non-prefixed version for the current environment in the shared state.`}</p>
    <p>{`Here is a modification of the previous example, which now includes `}<inlineCode parentName="p">{`build.environment`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  "build.environment": "dev",
  "myExtension.server": "mydomain.com",
  "__dev__myExtension.server": "mydomain.dev.com",
  "__stage__myExtension.server": "mydomain.stage.com"
}
`}</code></pre>
    <p>{`Here is the resulting shared state from the Configuration extension:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  "build.environment": "dev",
  "myExtension.server": "mydomain.dev.com"  
}
`}</code></pre>
    <h3 {...{
      "id": "sample-configuration"
    }}>{`Sample configuration`}</h3>
    <p>{`Here's a sample JSON file for the SDK:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
    "experienceCloud.org": "3CE342C75100435B0A490D4C@AdobeOrg",  
    "target.clientCode": "yourclientcode",  
    "target.timeout": 5,  
    "audience.server": "omniture.demdex.net",  
    "audience.timeout": 5,  
    "analytics.rsids": "mobilersidsample",  
    "analytics.server": "obumobile1.sc.omtrdc.net",  
    "analytics.aamForwardingEnabled": false,  
    "analytics.offlineEnabled": true,  
    "analytics.batchLimit": 0,  
    "analytics.backdatePreviousSessionInfo": false,
    "global.privacy": "optedin",  
    "lifecycle.sessionTimeout": 300,  
    "rules.url": "https://link.to.rules/test.zip"
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      